import React from "react";

import Stack from "@mui/material/Stack";
import { format, intlFormatDistance } from "date-fns";

import { TransitionDetailsDTO } from "@fartherfinance/frontend/api/TransitionWorkflows/Types";

import { selectFirstTransitionAdvisor } from "../../../utils";
import TransitionCard from "../../ui/TransitionCard";
import TransitionErrorState from "../../ui/TransitionErrorState";
import TransitionHeader from "../../ui/TransitionHeader";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Chip from "@src/sharedComponents/Chip/Chip";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";
import toUSD from "@src/utils/toUSD";

import styles from "./TransitionOverview.module.css";

const PLACEHOLDER = "--";

interface TransitionOverviewProps {
  transition: TransitionDetailsDTO | undefined;
  isLoading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

const TransitionOverview: React.FC<TransitionOverviewProps> = ({
  transition,
  isLoading = false,
  hasError = false,
  onRetry,
}) => {
  if (isLoading) {
    return (
      <Stack>
        <TransitionHeader title="Overview" />

        <div className={styles.cards}>
          <TransitionCard>
            <div className={styles.containerLeft}>
              <div className={styles.row}>
                <Skeleton variant="rounded" width="60%" height={24} />
              </div>
              <div className={styles.row}>
                <Skeleton variant="rounded" width="40%" height={16} />
              </div>
            </div>
          </TransitionCard>

          <TransitionCard hasPadding={false}>
            <div className={styles.containerMiddle}>
              <div className={toClassName(styles.row, styles.rowPadding)}>
                <Skeleton variant="rounded" width="50%" height={20} />
                <Skeleton variant="rounded" width="70%" height={24} />
              </div>
              <div className={toClassName(styles.row, styles.rowPadding)}>
                <Skeleton variant="rounded" width="60%" height={20} />
                <Skeleton variant="rounded" width="70%" height={24} />
              </div>
            </div>
          </TransitionCard>

          <TransitionCard paddingMedium>
            <div className={styles.containerRight}>
              <div className={styles.row}>
                <Skeleton variant="rounded" width="30%" height={16} />
                <Skeleton variant="rounded" width="50%" height={16} />
              </div>
              <div className={styles.row}>
                <Skeleton variant="rounded" width="20%" height={16} />
                <Skeleton variant="rounded" width="60%" height={16} />
              </div>
            </div>
          </TransitionCard>
        </div>
      </Stack>
    );
  }

  if (hasError) {
    return (
      <Stack>
        <TransitionHeader title="Overview" />
        <TransitionErrorState
          message="Could not load transition overview"
          onRetry={onRetry}
        />
      </Stack>
    );
  }

  const transitionAdvisor = selectFirstTransitionAdvisor(
    transition?.advisors ?? []
  );

  return (
    <Stack>
      <TransitionHeader title="Overview" />

      <div className={styles.cards}>
        <TransitionCard>
          <div className={styles.containerLeft}>
            <div className={styles.row}>
              {transition?.task.dueDate ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexGrow={1}
                >
                  <Text variant="title2" color="text">
                    {format(transition.task.dueDate, "MMMM d, yyyy")}
                  </Text>

                  <Chip
                    label={intlFormatDistance(
                      transition.task.dueDate,
                      new Date()
                    )}
                    backgroundColor="var(--surfaceMedium)"
                  />
                </Stack>
              ) : (
                <Text variant="title2" color="textSubtle">
                  {PLACEHOLDER}
                </Text>
              )}
            </div>

            <div className={styles.row}>
              <Text variant="body" color="textSubtle">
                Launch Date
              </Text>
            </div>
          </div>
        </TransitionCard>

        <TransitionCard hasPadding={false}>
          <div className={styles.containerMiddle}>
            <div className={toClassName(styles.row, styles.rowPadding)}>
              <Text variant="title3" color="text">
                Expected AUM
              </Text>
              {transition?.expectedAum ? (
                <Text variant="title2" color="text">
                  {toUSD(transition.expectedAum, { minimumFractionDigits: 0 })}
                </Text>
              ) : (
                <Text variant="title2" color="textSubtle">
                  {PLACEHOLDER}
                </Text>
              )}
            </div>

            <div className={toClassName(styles.row, styles.rowPadding)}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Text variant="title3" color="text">
                  Transitioned AUM
                </Text>
                {transition?.transitionedAumPercent && (
                  <Text variant="bodySmall" color="textSubtle">
                    ({transition.transitionedAumPercent}%)
                  </Text>
                )}
              </Stack>

              {transition?.transitionedAum ? (
                <Text variant="title2" color="text">
                  {toUSD(transition.transitionedAum, {
                    minimumFractionDigits: 0,
                  })}
                </Text>
              ) : (
                <Text variant="title2" color="textSubtle">
                  {PLACEHOLDER}
                </Text>
              )}
            </div>
          </div>
        </TransitionCard>

        <TransitionCard paddingMedium>
          <div className={styles.containerRight}>
            <div className={styles.row}>
              <Text variant="body" color="textSubtle">
                Advisor
              </Text>
              {transitionAdvisor ? (
                <Text variant="body" color="text">
                  {transitionAdvisor.fullName}
                </Text>
              ) : (
                <Text variant="body" color="textSubtle">
                  {PLACEHOLDER}
                </Text>
              )}
            </div>

            <div className={styles.row}>
              <Text variant="body" color="textSubtle">
                Email
              </Text>
              {transitionAdvisor ? (
                <a href={`mailto:${transitionAdvisor.emailAddress}`}>
                  <Text variant="body" color="text">
                    {transitionAdvisor.emailAddress}
                  </Text>
                </a>
              ) : (
                <Text variant="body" color="textSubtle">
                  {PLACEHOLDER}
                </Text>
              )}
            </div>

            {/* 
            <div className={styles.row}>
              <Text variant="body" color="textSubtle">
                Phone
              </Text>
              <Text variant="body" color="text">
                (555) 555-5555
              </Text>
            </div>
            */}
          </div>
        </TransitionCard>
      </div>
    </Stack>
  );
};

export default TransitionOverview;
